/**
 * Bestek invite tracking (mail opened, link clicked, page visited) and the
 * automatic reminder schedule. Shared so the reminder job, the dashboard and
 * the bestek modal all read the same numbers.
 */

const {
  BESTEK_STATUS,
  getBestekStatus,
  getBestekExpiresAt,
  hasBestekCustomerChoice,
  hasOpenBestekIncompleteness,
} = require("../types/bestek.types");

const DAY_MS = 24 * 60 * 60 * 1000;

const BESTEK_MAIL_KIND = {
  INVITE: "invite",
  UPDATE: "update",
  REOPENED: "reopened",
  REMINDER: "reminder",
};

const BESTEK_REMINDER_STAGE = {
  AFTER_SEND: "after_send",
  BEFORE_EXPIRY: "before_expiry",
  MANUAL: "manual",
};

/** Automatic stages, in order. Empty array = manual reminders only. */
const BESTEK_AUTO_REMINDER_STAGES = [
  BESTEK_REMINDER_STAGE.AFTER_SEND,
  BESTEK_REMINDER_STAGE.BEFORE_EXPIRY,
];
const BESTEK_REMINDER_AFTER_SEND_DAYS = 7;
const BESTEK_REMINDER_BEFORE_EXPIRY_DAYS = 5;
/** Never two mails to the customer closer together than this. */
const BESTEK_REMINDER_MIN_GAP_DAYS = 2;

const toMs = (value) => {
  if (!value) return NaN;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : NaN;
};

const toDate = (ms) => (Number.isFinite(ms) ? new Date(ms) : null);

const minMs = (values) =>
  values.reduce((min, v) => (Number.isFinite(v) && (!Number.isFinite(min) || v < min) ? v : min), NaN);

const maxMs = (values) =>
  values.reduce((max, v) => (Number.isFinite(v) && (!Number.isFinite(max) || v > max) ? v : max), NaN);

const getCurrentVersion = (bestek) =>
  Number(bestek?.currentPublishedVersion) || 1;

/** Tracked mails of the version on the customer page, oldest first. */
const getCurrentVersionMails = (bestek) => {
  const version = getCurrentVersion(bestek);
  const mails = Array.isArray(bestek?.inviteMails) ? bestek.inviteMails : [];
  return mails
    .filter((m) => (Number(m?.version) || 1) === version)
    .sort((a, b) => toMs(a.sentAt) - toMs(b.sentAt));
};

const isReminderMail = (mail) => mail?.kind === BESTEK_MAIL_KIND.REMINDER;

/**
 * Last invite (not reminder) of the current version. Besteks mailed before
 * tracking existed only have customerEmailSentAt.
 */
const getLastInviteMs = (bestek, mails) => {
  const tracked = maxMs(mails.filter((m) => !isReminderMail(m)).map((m) => toMs(m.sentAt)));
  if (Number.isFinite(tracked)) return tracked;
  return toMs(bestek?.customerEmailSentAt);
};

/**
 * Who gets reminders: the list service set on the bestek, else the contact.
 * An empty set list means reminders are off for this bestek.
 */
const getBestekReminderEmails = (bestek) => {
  const custom = bestek?.reminderEmails;
  return String(custom != null ? custom : bestek?.contactEmail || "").trim();
};

const hasCustomBestekReminderEmails = (bestek) => bestek?.reminderEmails != null;

/** Waiting on the customer: reminders make sense. */
const isBestekReminderEligible = (bestek, now = Date.now()) => {
  if (!bestek) return false;
  if (getBestekStatus(bestek) !== BESTEK_STATUS.PROPOSED) return false;
  if (hasBestekCustomerChoice(bestek)) return false;
  if (hasOpenBestekIncompleteness(bestek)) return false;
  if (bestek.customerEmailSkipped) return false;
  if (bestek.bestekType === "administrative") return false;
  if (!getBestekReminderEmails(bestek)) return false;
  const expiresAt = getBestekExpiresAt(bestek);
  if (!expiresAt || toMs(now) >= expiresAt.getTime()) return false;
  return Number.isFinite(getLastInviteMs(bestek, getCurrentVersionMails(bestek)));
};

/**
 * Next automatic reminder for the current version, or null when none is
 * planned. dueAt may lie in the past (the job sends it on its next run).
 */
const getNextBestekReminder = (
  bestek,
  { now = Date.now(), stages = BESTEK_AUTO_REMINDER_STAGES } = {}
) => {
  if (!stages.length || !isBestekReminderEligible(bestek, now)) return null;
  const mails = getCurrentVersionMails(bestek);
  const expiresMs = getBestekExpiresAt(bestek).getTime();
  const lastInviteMs = getLastInviteMs(bestek, mails);
  const lastMailMs = maxMs([lastInviteMs, ...mails.map((m) => toMs(m.sentAt))]);
  const autoReminders = mails.filter((m) => isReminderMail(m) && m.auto);
  const beforeExpiryMs = expiresMs - BESTEK_REMINDER_BEFORE_EXPIRY_DAYS * DAY_MS;

  const pending = [];
  if (stages.includes(BESTEK_REMINDER_STAGE.AFTER_SEND)) {
    const sentSinceInvite = autoReminders.some(
      (m) =>
        m.reminderStage === BESTEK_REMINDER_STAGE.AFTER_SEND &&
        toMs(m.sentAt) >= lastInviteMs
    );
    const dueMs = lastInviteMs + BESTEK_REMINDER_AFTER_SEND_DAYS * DAY_MS;
    // Too close to expiry: the before-expiry reminder covers it.
    const coveredByExpiry =
      stages.includes(BESTEK_REMINDER_STAGE.BEFORE_EXPIRY) && dueMs >= beforeExpiryMs;
    if (!sentSinceInvite && !coveredByExpiry) {
      pending.push({ stage: BESTEK_REMINDER_STAGE.AFTER_SEND, dueMs });
    }
  }
  if (stages.includes(BESTEK_REMINDER_STAGE.BEFORE_EXPIRY)) {
    // Extending the validity plans a new before-expiry reminder.
    const sentForThisExpiry = autoReminders.some(
      (m) =>
        m.reminderStage === BESTEK_REMINDER_STAGE.BEFORE_EXPIRY &&
        toMs(m.forExpiresAt) === expiresMs
    );
    if (!sentForThisExpiry) {
      pending.push({ stage: BESTEK_REMINDER_STAGE.BEFORE_EXPIRY, dueMs: beforeExpiryMs });
    }
  }
  if (!pending.length) return null;

  pending.sort((a, b) => a.dueMs - b.dueMs);
  const next = pending[0];
  const dueMs = Math.max(next.dueMs, lastMailMs + BESTEK_REMINDER_MIN_GAP_DAYS * DAY_MS);
  if (dueMs >= expiresMs) return null;
  return { stage: next.stage, dueAt: new Date(dueMs), forExpiresAt: new Date(expiresMs) };
};

/** Reminder the job should send now, or null. */
const getDueBestekReminder = (bestek, options = {}) => {
  const now = toMs(options.now ?? Date.now());
  const next = getNextBestekReminder(bestek, { ...options, now });
  if (!next || next.dueAt.getTime() > now) return null;
  return next;
};

const summarizeTimes = (values) => {
  const ms = values.map(toMs).filter(Number.isFinite);
  return {
    count: ms.length,
    firstAt: toDate(minMs(ms)),
    lastAt: toDate(maxMs(ms)),
  };
};

/**
 * Tracking for the version on the customer page. Opens come from a pixel and
 * are only an indication (blocked by many mail apps, faked by Apple Mail).
 */
const getBestekTrackingSummary = (bestek, { now = Date.now() } = {}) => {
  const version = getCurrentVersion(bestek);
  const mails = getCurrentVersionMails(bestek);
  const reminders = mails.filter(isReminderMail);
  const visits = (Array.isArray(bestek?.pageVisits) ? bestek.pageVisits : []).filter(
    (v) => (Number(v?.version) || 1) === version
  );
  const lastInviteMs = getLastInviteMs(bestek, mails);
  const expiresAt = getBestekExpiresAt(bestek);
  return {
    version,
    sentAt: toDate(lastInviteMs),
    mailCount: mails.length,
    opened: summarizeTimes(mails.flatMap((m) => m.opens || [])),
    clicked: summarizeTimes(mails.flatMap((m) => m.clicks || [])),
    visited: summarizeTimes(visits.map((v) => v.at)),
    reminders: {
      ...summarizeTimes(reminders.map((m) => m.sentAt)),
      autoCount: reminders.filter((m) => m.auto).length,
    },
    expiresAt,
    reminderEmails: getBestekReminderEmails(bestek),
    reminderEmailsCustom: hasCustomBestekReminderEmails(bestek),
    isExpired: Boolean(expiresAt && toMs(now) > expiresAt.getTime() && !hasBestekCustomerChoice(bestek)),
    nextReminder: getNextBestekReminder(bestek, { now }),
  };
};

module.exports = {
  BESTEK_MAIL_KIND,
  BESTEK_REMINDER_STAGE,
  BESTEK_AUTO_REMINDER_STAGES,
  BESTEK_REMINDER_AFTER_SEND_DAYS,
  BESTEK_REMINDER_BEFORE_EXPIRY_DAYS,
  BESTEK_REMINDER_MIN_GAP_DAYS,
  getCurrentVersionMails,
  getBestekReminderEmails,
  hasCustomBestekReminderEmails,
  isBestekReminderEligible,
  getNextBestekReminder,
  getDueBestekReminder,
  getBestekTrackingSummary,
};
